import { useEffect, useCallback } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  notificationService,
  type NotificationItem,
} from "../services/notification.service";
import { onNotification } from "../lib/events-client";
import { backgroundDbWorkRefetchIntervalMs } from "../lib/background-db-work-pause";
import { useAuthStore } from "../stores/authStore";

const NOTIFICATIONS_KEY = ["notifications"];
const UNREAD_COUNT_KEY = ["notifications", "unread-count"];

export function useNotifications() {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  // Fetch recent notifications
  const { data: notifications = [], isLoading } = useQuery<NotificationItem[]>({
    queryKey: NOTIFICATIONS_KEY,
    queryFn: async () => {
      const res = await notificationService.list({ limit: 20 });
      return res.items;
    },
    enabled: isAuthenticated,
    refetchInterval: () => backgroundDbWorkRefetchIntervalMs(60_000),
  });

  // Fetch unread count
  const { data: unreadCount = 0 } = useQuery<number>({
    queryKey: UNREAD_COUNT_KEY,
    queryFn: () => notificationService.getUnreadCount(),
    enabled: isAuthenticated,
    refetchInterval: () => backgroundDbWorkRefetchIntervalMs(60_000),
  });

  // Subscribe to real-time notification events
  useEffect(() => {
    if (!isAuthenticated) return;

    const unsubscribe = onNotification((incoming) => {
      // Prepend to notifications list
      queryClient.setQueryData<NotificationItem[]>(NOTIFICATIONS_KEY, (old) => {
        const newNotif: NotificationItem = {
          id: incoming.id,
          tenantId: "",
          userId: null,
          title: incoming.title,
          message: incoming.message,
          type: incoming.type,
          read: false,
          data: incoming.data || null,
          createdAt: incoming.createdAt,
        };
        return [newNotif, ...(old || [])].slice(0, 50);
      });

      // Increment unread count
      queryClient.setQueryData<number>(
        UNREAD_COUNT_KEY,
        (old) => (old || 0) + 1,
      );
    });

    return unsubscribe;
  }, [isAuthenticated, queryClient]);

  // Mark single notification as read
  const markRead = useMutation({
    mutationFn: (id: string) => notificationService.markRead(id),
    onMutate: async (id) => {
      // Optimistic update
      queryClient.setQueryData<NotificationItem[]>(NOTIFICATIONS_KEY, (old) =>
        (old || []).map((n) => (n.id === id ? { ...n, read: true } : n)),
      );
      queryClient.setQueryData<number>(UNREAD_COUNT_KEY, (old) =>
        Math.max((old || 1) - 1, 0),
      );
    },
  });

  // Mark all as read
  const markAllRead = useMutation({
    mutationFn: () => notificationService.markAllRead(),
    onMutate: async () => {
      queryClient.setQueryData<NotificationItem[]>(NOTIFICATIONS_KEY, (old) =>
        (old || []).map((n) => ({ ...n, read: true })),
      );
      queryClient.setQueryData<number>(UNREAD_COUNT_KEY, () => 0);
    },
  });

  const handleMarkRead = useCallback(
    (id: string) => markRead.mutate(id),
    [markRead],
  );

  const handleMarkAllRead = useCallback(
    () => markAllRead.mutate(),
    [markAllRead],
  );

  return {
    notifications,
    unreadCount,
    isLoading,
    markRead: handleMarkRead,
    markAllRead: handleMarkAllRead,
  };
}
